// ─── Collage Element: Title Block ─────────────────────────────
// Hero title cluster or footer closing message.
// Applies customStyle overrides (font, size, color, align) when present.

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';
import { useCollageElementMotion } from '../../../lib/utils/collageMotion';

function HeroTitleCluster({ element, theme }) {
  const cs = element.customStyle || {};
  return (
    <div style={{ textAlign: cs.textAlign || 'center' }}>
      <h1
        style={{
          fontFamily: cs.fontFamily || theme.headingFont,
          fontSize: cs.fontSize || undefined,
          color: cs.color || '#ffffff',
          textShadow: '0 10px 32px rgba(0,0,0,0.25)',
          lineHeight: cs.lineHeight || 1,
          letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
          textTransform: cs.textTransform || undefined,
        }}
        className={!cs.fontSize ? 'text-[44px] sm:text-[76px]' : undefined}
      >
        {element.title}
      </h1>
      {element.subtitle && (
        <p
          className="mx-auto mt-4 max-w-2xl leading-7"
          style={{
            fontFamily: cs.fontFamily || theme.bodyFont,
            fontSize: cs.fontSize ? `${Math.max(12, parseInt(cs.fontSize) * 0.45)}px` : undefined,
            color: cs.color ? `${cs.color}ee` : 'rgba(255,255,255,0.9)',
          }}
        >
          {element.subtitle}
        </p>
      )}
      {element.meta && (
        <p
          className="mt-5 uppercase tracking-[0.2em]"
          style={{
            fontFamily: theme.bodyFont,
            fontSize: cs.fontSize ? `${Math.max(10, parseInt(cs.fontSize) * 0.28)}px` : '11px',
            color: cs.color ? `${cs.color}dd` : 'rgba(255,255,255,0.85)',
          }}
        >
          {element.meta}
        </p>
      )}
      {element.buttonText && (
        <button
          onClick={element.buttonHref?.startsWith('#') ? () => {
            const el = document.getElementById(element.buttonHref.slice(1));
            el?.scrollIntoView({ behavior: 'smooth' });
          } : undefined}
          className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-3 text-sm text-white backdrop-blur-sm transition hover:bg-white/15"
          style={{ fontFamily: theme.bodyFont }}
        >
          {element.buttonText}
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function FooterTitleCluster({ element, theme }) {
  const cs = element.customStyle || {};
  return (
    <div style={{ textAlign: cs.textAlign || 'center' }}>
      <h2
        style={{
          fontFamily: cs.fontFamily || theme.headingFont,
          fontSize: cs.fontSize || undefined,
          color: cs.color || theme.textPrimary,
          lineHeight: cs.lineHeight || 1.15,
          letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
          textTransform: cs.textTransform || undefined,
        }}
        className={!cs.fontSize ? 'text-[34px] sm:text-[56px]' : undefined}
      >
        {element.title}
      </h2>
      {element.subtitle && (
        <p
          className="mt-4 uppercase tracking-[0.22em]"
          style={{
            fontFamily: theme.bodyFont,
            fontSize: cs.fontSize ? `${Math.max(10, parseInt(cs.fontSize) * 0.3)}px` : '11px',
            color: cs.color || theme.textSecondary,
          }}
        >
          {element.subtitle}
        </p>
      )}
    </div>
  );
}

export default function TitleBlock({ element, isMobile, theme, rsvpRef, skipPositioning, isEditing, onEditText }) {
  const style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const motionProps = useCollageElementMotion(element);
  const cs = element.customStyle || {};

  const handleTitleBlur = (e) => {
    if (onEditText) onEditText('title', e.currentTarget.innerText);
  };
  const handleSubtitleBlur = (e) => {
    if (onEditText) onEditText('subtitle', e.currentTarget.innerText);
  };

  return (
    <motion.div {...motionProps} style={style} className={isEditing ? 'select-text' : 'select-none'}>
      {element.style === 'hero-title' && (
        isEditing ? (
          <div style={{ textAlign: cs.textAlign || 'center' }}>
            <h1
              contentEditable
              suppressContentEditableWarning
              onBlur={handleTitleBlur}
              onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
              className={`outline-none ring-1 ring-blue-400/50 rounded px-1 -mx-1 ${!cs.fontSize ? 'text-[44px] sm:text-[76px]' : ''}`}
              style={{
                fontFamily: cs.fontFamily || theme.headingFont,
                fontSize: cs.fontSize || undefined,
                color: cs.color || '#ffffff',
                textShadow: '0 10px 32px rgba(0,0,0,0.25)',
                lineHeight: cs.lineHeight || 1,
                letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
                textTransform: cs.textTransform || undefined,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                minHeight: '1em',
              }}
              data-testid={`inline-edit-title-${element.id}`}
            >
              {element.title}
            </h1>
            {(element.subtitle || isEditing) && (
              <p
                contentEditable
                suppressContentEditableWarning
                onBlur={handleSubtitleBlur}
                onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
                className="mx-auto mt-4 max-w-2xl leading-7 outline-none ring-1 ring-blue-400/30 rounded px-1 -mx-1"
                style={{
                  fontFamily: cs.fontFamily || theme.bodyFont,
                  fontSize: cs.fontSize ? `${Math.max(12, parseInt(cs.fontSize) * 0.45)}px` : undefined,
                  color: cs.color ? `${cs.color}ee` : 'rgba(255,255,255,0.9)',
                  minHeight: '1em',
                }}
                data-testid={`inline-edit-subtitle-${element.id}`}
              >
                {element.subtitle || 'Subtitle...'}
              </p>
            )}
          </div>
        ) : (
          <HeroTitleCluster element={element} theme={theme} rsvpRef={rsvpRef} />
        )
      )}
      {element.style === 'footer-title' && (
        isEditing ? (
          <div style={{ textAlign: cs.textAlign || 'center' }}>
            <h2
              contentEditable
              suppressContentEditableWarning
              onBlur={handleTitleBlur}
              onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
              className={`outline-none ring-1 ring-blue-400/50 rounded px-1 -mx-1 ${!cs.fontSize ? 'text-[34px] sm:text-[56px]' : ''}`}
              style={{
                fontFamily: cs.fontFamily || theme.headingFont,
                fontSize: cs.fontSize || undefined,
                color: cs.color || theme.textPrimary,
                lineHeight: cs.lineHeight || 1.15,
                letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
                textTransform: cs.textTransform || undefined,
                minHeight: '1em',
              }}
              data-testid={`inline-edit-title-${element.id}`}
            >
              {element.title}
            </h2>
          </div>
        ) : (
          <FooterTitleCluster element={element} theme={theme} />
        )
      )}
    </motion.div>
  );
}
